import { Avatar, Button, Divider, Stack, Typography, useMediaQuery } from "@mui/material"
import { IconLike } from "../../../../icons/IconLike"
import { COLORS } from "../../../../styles/variables"
import {
    AvatarStyle,
    ButtonStyle,
    replyRowIndentDepth1,
    replyRowIndentFlat,
    replyRowIndentNested,
    StackWithComment,
    TypAnswer,
    TypDate,
    TypNameLastName,
    TypText,
} from "./style"
import { formatNumber } from "../../../../common/FormatNumber"
import { PureLightTheme } from "../../../../styles/theme"
import { MAX_VISIBLE_REPLY_THREAD_DEPTH, ReplyData } from "../../../../types"
import { formatRepliesCountRu } from "../../../../utils/repliesRu"
import { formatApiDateTimeComment } from "../../../../utils/common"
import { CommentEditableText, type CommentEditState } from "../CommentEditableText"
import { useSetCommentReactionMutation } from "../../../../api/projects"
import { useSetGrowthToolCommentReactionMutation } from "../../../../api/growthTools"
import { useSetSocialOrderCommentReactionMutation } from "../../../../api/socialOrders"
import type { CommentsSortUi, IGetCommentResponse } from "../../../../api/types"
import { IconLikeFilled } from "../../../../icons/IconLikeFilled"
import { getCommentHighlightRowSx } from "../../helpers/commentHighlightSx"
import { openPlatformReportModal } from "../../../Modals"

export type OpenL2ReplyThreadPayload = {
    anchorCommentId: number
    excerpt: string
    replyCount: number
    /** После открытия модалки сразу активировать ответ на якорь L2 (в ленте нет узла треда). */
    startReplyComposer?: boolean
    anchorFirstName?: string
    anchorLastName?: string
    highlightCommentId?: number
}

type ReplyItemProps = {
    reply: ReplyData
    replyDepth: number
    onReplyTo: (parentId: number, firstName: string, lastName: string) => void
    loadMoreReplies?: (parentId: number) => void | Promise<void>
    loadingParentId?: number | null
    currentUserId?: number | null
    editState: CommentEditState
    onBeginEdit: (commentId: number, originalText: string) => void
    cancelEdit: () => void
    setDraft: (value: string) => void
    confirmSave: () => void | Promise<void>
    isUpdatingComment: boolean
    onDeleteComment?: (commentId: number) => Promise<boolean>
    /** Плоская карточка в модалке треда: без дерева под узлом. */
    flatList?: boolean
    /** Открыть обсуждение под этим узлом L2 (лента проекта). */
    onOpenReplyThread?: (p: OpenL2ReplyThreadPayload) => void
    commentReactionCache?:
        | { kind?: 'project'; projectId: number; type: string; sort?: CommentsSortUi; threadParentId?: number }
        | { kind: 'growthTool'; growthToolId: number; type: string; sort?: CommentsSortUi; threadParentId?: number }
        | { kind: 'socialOrder'; socialOrderId: number; type: string; sort?: CommentsSortUi; threadParentId?: number }
    /** Модалка треда: локальный список не из getComments — обновить строку после лайка. */
    onCommentReactionSuccess?: (node: IGetCommentResponse) => void
    highlightCommentId?: number | null
}

export const ReplyItem = ({
    reply,
    replyDepth,
    onReplyTo,
    loadMoreReplies,
    loadingParentId,
    currentUserId,
    editState,
    onBeginEdit,
    cancelEdit,
    setDraft,
    confirmSave,
    isUpdatingComment,
    onDeleteComment,
    flatList = false,
    onOpenReplyThread,
    commentReactionCache,
    onCommentReactionSuccess,
    highlightCommentId,
}: ReplyItemProps) => {
    const highlightSx = getCommentHighlightRowSx(highlightCommentId === reply.id)
    const isTablet = useMediaQuery(PureLightTheme.breakpoints.down("md"))
    const rowIndentSx = flatList
        ? replyRowIndentFlat
        : replyDepth >= 2
          ? replyRowIndentNested
          : replyRowIndentDepth1

    const threadCount = reply.threadChildrenCount ?? 0
    /** Полный текст в модалку; обрезка только по длине для разумного DOM. */
    const threadAnchorExcerpt =
        reply.text.length > 800 ? `${reply.text.slice(0, 797).trimEnd()}…` : reply.text
    const [setProjectReaction, { isLoading: isProjectReacting }] = useSetCommentReactionMutation()
    const [setGrowthToolReaction, { isLoading: isGrowthToolReacting }] = useSetGrowthToolCommentReactionMutation()
    const [setSocialOrderReaction, { isLoading: isSocialOrderReacting }] = useSetSocialOrderCommentReactionMutation()
    const isReacting = isProjectReacting || isGrowthToolReacting || isSocialOrderReacting

    const isEditing = editState?.commentId === reply.id
    const canEdit = currentUserId != null && reply.authorId === currentUserId
    const canDelete = reply.canDelete === true

    const handleDelete = async () => {
        if (!onDeleteComment) return
        await onDeleteComment(reply.id)
    }

    const handleHeartToggle = async () => {
        const value = reply.likedByMe ? 0 : 1
        try {
            const updated = commentReactionCache?.kind === 'growthTool'
                ? await setGrowthToolReaction({
                    commentId: reply.id,
                    body: { value },
                    cache: commentReactionCache,
                }).unwrap()
                : commentReactionCache?.kind === 'socialOrder'
                  ? await setSocialOrderReaction({
                      commentId: reply.id,
                      body: { value },
                      cache: commentReactionCache,
                  }).unwrap()
                  : await setProjectReaction({
                    commentId: reply.id,
                    body: { value },
                    ...(commentReactionCache ? { cache: commentReactionCache } : {}),
                }).unwrap()
            onCommentReactionSuccess?.(updated)
        } catch {
            // no-op
        }
    }

    const handleAnswerClick = () => {
        if (!flatList && replyDepth === 1 && onOpenReplyThread) {
            onOpenReplyThread({
                anchorCommentId: reply.id,
                excerpt: threadAnchorExcerpt,
                replyCount: threadCount,
                startReplyComposer: true,
                anchorFirstName: reply.firstName,
                anchorLastName: reply.lastName,
            })
            return
        }
        onReplyTo(reply.id, reply.firstName, reply.lastName)
    }

    return (
        <Stack id={`comment-${reply.id}`} sx={highlightSx} gap={0}>
            <Stack direction="row" sx={rowIndentSx}>
                <Avatar src={reply.avatarUrl} sx={AvatarStyle} />
                <Stack sx={StackWithComment}>
                    {reply.inReplyToLine ? (
                        <Typography variant="pictonBlue-12-normal" sx={{ mb: 0.25, fontStyle: "italic" }}>
                            {reply.inReplyToLine}
                        </Typography>
                    ) : null}
                    <Typography variant="cerulean-14-semiBold" sx={TypNameLastName}>
                        {reply.firstName} {reply.lastName}
                    </Typography>
                    <CommentEditableText
                        nodeId={reply.id}
                        text={reply.text}
                        editState={editState}
                        setDraft={setDraft}
                        onConfirmSave={confirmSave}
                        onCancelEdit={cancelEdit}
                        isUpdatingComment={isUpdatingComment}
                        expandableSx={TypText}
                    />
                    {isTablet && (
                        <Typography variant="pictonBlue-12-normal" sx={TypDate}>
                            {formatApiDateTimeComment(reply.date)}
                        </Typography>
                    )}
                    <Stack direction="row" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" gap={2} flexWrap="wrap">
                            {!isTablet && (
                                <Typography variant="pictonBlue-12-normal" sx={TypDate}>
                                    {formatApiDateTimeComment(reply.date)}
                                </Typography>
                            )}
                            {!isEditing && (
                                <>
                                    {!flatList && replyDepth === 1 && threadCount > 0 && onOpenReplyThread ? (
                                        <Typography
                                            variant="pictonBlue-12-normal"
                                            sx={{ ...TypAnswer, fontWeight: 500 }}
                                            onClick={() =>
                                                onOpenReplyThread({
                                                    anchorCommentId: reply.id,
                                                    excerpt: threadAnchorExcerpt,
                                                    replyCount: threadCount,
                                                })
                                            }
                                        >
                                            Открыть переписку — {formatRepliesCountRu(threadCount)}
                                        </Typography>
                                    ) : null}
                                    {replyDepth < MAX_VISIBLE_REPLY_THREAD_DEPTH ? (
                                        <Typography
                                            variant="pictonBlue-12-normal"
                                            sx={TypAnswer}
                                            onClick={handleAnswerClick}
                                        >
                                            Ответить
                                        </Typography>
                                    ) : null}
                                    {canEdit ? (
                                        <Typography
                                            variant="pictonBlue-12-normal"
                                            sx={TypAnswer}
                                            onClick={() => onBeginEdit(reply.id, reply.text)}
                                        >
                                            Изменить
                                        </Typography>
                                    ) : null}
                                    {canDelete ? (
                                        <Typography
                                            variant="pictonBlue-12-normal"
                                            sx={TypAnswer}
                                            onClick={() => void handleDelete()}
                                        >
                                            Удалить
                                        </Typography>
                                    ) : null}
                                    {currentUserId != null && reply.authorId !== currentUserId ? (
                                        <Typography
                                            variant="pictonBlue-12-normal"
                                            sx={TypAnswer}
                                            onClick={() =>
                                                openPlatformReportModal({
                                                    targetType: 'comment',
                                                    targetId: reply.id,
                                                    dialogTitle: 'Пожаловаться на комментарий',
                                                })
                                            }
                                        >
                                            Пожаловаться
                                        </Typography>
                                    ) : null}
                                </>
                            )}
                        </Stack>
                        <Stack direction="row" gap={2}>
                            <Button
                                variant="text"
                                disableRipple
                                sx={ButtonStyle}
                                disabled={isReacting}
                                onClick={() => void handleHeartToggle()}
                                aria-label={reply.likedByMe ? "Снять лайк" : "Поставить лайк"}
                            >
                                <Stack direction="row" gap={0.5} alignItems="center">
                                    {reply.likedByMe ? 
                                        <IconLikeFilled sx={{ width: 16, height: 16, color: COLORS.CERULEAN }} /> : 
                                        <IconLike sx={{ width: 16, height: 16, color: COLORS.CERULEAN }} />
                                    }
                                    {reply.likes > 0 ? (
                                        <Typography 
                                            variant="pictonBlue-12-medium"
                                            sx={{ lineHeight: 0 }}
                                        >{formatNumber(reply.likes)}</Typography>
                                    ) : null}
                                </Stack>
                            </Button>
                        </Stack>
                    </Stack>
                    <Divider sx={{ mt: "3px" }} />
                </Stack>
            </Stack>
            {!flatList && replyDepth < MAX_VISIBLE_REPLY_THREAD_DEPTH
                ? reply.replies?.map((nested) => (
                      <ReplyItem
                          key={nested.id}
                          reply={nested}
                          replyDepth={replyDepth + 1}
                          onReplyTo={onReplyTo}
                          loadMoreReplies={loadMoreReplies}
                          loadingParentId={loadingParentId}
                          currentUserId={currentUserId}
                          editState={editState}
                          onBeginEdit={onBeginEdit}
                          cancelEdit={cancelEdit}
                          setDraft={setDraft}
                          confirmSave={confirmSave}
                          isUpdatingComment={isUpdatingComment}
                          onDeleteComment={onDeleteComment}
                          onOpenReplyThread={onOpenReplyThread}
                          commentReactionCache={commentReactionCache}
                          onCommentReactionSuccess={onCommentReactionSuccess}
                          highlightCommentId={highlightCommentId}
                      />
                  ))
                : null}
            {!flatList && reply.hasMoreChildren && loadMoreReplies ? (
                <Stack sx={{ ...rowIndentSx, mt: 0.5 }}>
                    <Button
                        variant="text"
                        disableRipple
                        sx={{ ...ButtonStyle, textTransform: "none", justifyContent: "flex-start" }}
                        disabled={loadingParentId === reply.id}
                        onClick={() => void loadMoreReplies(reply.id)}
                    >
                        <Typography variant="pictonBlue-12-normal" sx={TypAnswer}>
                            Показать ещё
                        </Typography>
                    </Button>
                </Stack>
            ) : null}
        </Stack>
    )
}
